import React from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ChevronDown, ChevronRight, Link2, Sparkles } from "lucide-react";
import { DEFAULT_CATEGORIES } from "@/lib/column-manager";
import type { SimpleColumn } from "@/lib/column-manager";

interface ColumnCategoryProps {
  categoryKey: string;
  categoryColumns: SimpleColumn[];
  isExpanded: boolean;
  onToggleExpansion: (category: string) => void;
  onToggleCategory: (category: string) => void;
  onToggleColumn: (columnId: string) => void;
  getCategoryStats: (category: string) => { visible: number; total: number };
}

export function ColumnCategory({
  categoryKey,
  categoryColumns,
  isExpanded,
  onToggleExpansion,
  onToggleCategory,
  onToggleColumn,
  getCategoryStats,
}: ColumnCategoryProps) {
  const categoryInfo = DEFAULT_CATEGORIES[categoryKey as keyof typeof DEFAULT_CATEGORIES];
  if (!categoryInfo || categoryColumns.length === 0) return null;

  const stats = getCategoryStats(categoryKey);
  const getForeignKeyTooltip = (column: SimpleColumn): string => {
    if (!column.foreignKey) {
      return "Linked field: this value comes from another related record.";
    }
    return `Linked field: this value connects to a related record. Source: ${column.foreignKey.referencedTable}.${column.foreignKey.referencedColumn}.`;
  };

  const getDerivedTooltip = (column: SimpleColumn): string =>
    column.readOnlyReason ||
    column.dataLineage?.notes ||
    "Calculated field: this value is auto-filled and read-only.";

  return (
    <div className="border rounded-lg">
      <div
        role="button"
        tabIndex={0}
        onClick={() => onToggleExpansion(categoryKey)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggleExpansion(categoryKey);
          }
        }}
        className="w-full p-3 flex items-center justify-between hover:bg-card-50 cursor-pointer"
      >
        <div className="flex items-center gap-2">
          <span className={`px-2 py-1 rounded text-xs font-medium ${categoryInfo.color}`}>
            {categoryInfo.label}
          </span>
          <span className="text-sm text-gray-500">
            ({stats.visible}/{stats.total})
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onToggleCategory(categoryKey);
            }}
          >
            {stats.visible === stats.total ? "Hide All" : "Show All"}
          </Button>
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronRight className="h-4 w-4 text-gray-400" />
          )}
        </div>
      </div>

      {isExpanded && (
        <div className="p-3 space-y-2 border-t">
          {categoryColumns.map((column) => (
            <label key={column.id} className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={column.visible}
                onCheckedChange={() => onToggleColumn(column.id)}
                aria-checked={column.visible}
              />
              <div className="min-w-0">
                <div className="flex min-w-0 items-center gap-2">
                  <span className={`text-sm ${column.visible ? "text-card-foreground" : "text-gray-500"}`}>
                    {column.label}
                  </span>
                  {column.isForeignKey ? (
                    <span
                      className="inline-flex items-center gap-1 rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200"
                      title={getForeignKeyTooltip(column)}
                    >
                      <Link2 className="h-3 w-3" />
                      FK
                    </span>
                  ) : null}
                  {column.isReadOnlyDerived ? (
                    <span
                      className="inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
                      title={getDerivedTooltip(column)}
                    >
                      <Sparkles className="h-3 w-3" />
                      Derived
                    </span>
                  ) : null}
                </div>
                {column.isForeignKey && column.foreignKey ? (
                  <div className="text-xs text-gray-500">
                    {column.id}
                    {" -> "}
                    {column.foreignKey.referencedTable}
                    {"."}
                    {column.foreignKey.referencedColumn}
                  </div>
                ) : null}
              </div>
              {column.priority <= 3 && (
                <span className="text-xs bg-blue-100 text-blue-800 px-1 rounded dark:bg-blue-900/40 dark:text-blue-200">
                  Essential
                </span>
              )}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
